import { Berufsabschluss } from '../enums/Berufsabschluss.js';
import { Schulabschluss } from '../enums/Schulabschluss.js';
import { getTaetigkeitsTestIdentifikationscode } from '../enums/Taetigkeiten.js';
import { Vertragsform } from '../enums/Vertragsform.js';
import { getIdentifikationscode } from './getIdentificationCodeForTaetigkeitsData.js';

export function replaceAtIndexRange(
  str: string,
  replacement: string,
  startIndexInclusive: number,
  endIndexInclusive?: number,
): string {
  return (
    str.substring(0, startIndexInclusive) + replacement + str.substring((endIndexInclusive ?? startIndexInclusive) + 1)
  );
}

export const TAETIGKEITSSCHLUESSEL_INDEX_MAP = {
  taetigkeit: [0, 4],
  schulabschluss: [5, 5],
  berufsabschluss: [6, 6],
  arbeitnehmerueberlassung: [7, 7],
  vertragsform: [8, 8],
} as const satisfies Record<string, readonly [number, number]>;

export type TaetigkeitsschluesselFields = keyof typeof TAETIGKEITSSCHLUESSEL_INDEX_MAP;

export function deconstructTaetigkeitsschluessel(schluessel: string): Record<TaetigkeitsschluesselFields, string> {
  return Object.fromEntries(
    Object.entries(TAETIGKEITSSCHLUESSEL_INDEX_MAP).map(([key, [start, end]]) => [
      key,
      schluessel.slice(start, end + 1),
    ]),
  ) as Record<TaetigkeitsschluesselFields, string>;
}

/**
 * Calculates the Tätigkeitsschlüssel based on the provided parameters. The Tätigkeitsschlüssel can be seen as a hash for employment statistics. It is a condensed form of the employment data of a contract and its employee.
 * For example it contains information about the education level and the type of employment.
 *
 * How this key is generated can be found in the official documentation:
 * https://www.arbeitsagentur.de/datei/schluesselverzeichnis-fuer-die-angaben-zur-taetigkeit_ba037939.pdf
 *
 * @param params The parameters to calculate the Tätigkeitsschlüssel with.
 * @returns The calculated Tätigkeitsschlüssel.
 */
export function calculateTaetigkeitsschluessel(params: {
  taetigkeit: string;
  hoechsterSchulabschluss?: string;
  hoechsterBerufsabschluss?: string;
  vertragsform?: string;
  arbeitnehmerueberlassung?: boolean;
}): string {
  /**
   * Normally martinus has no external contract workers (Arbeitnehmerüberlassung).
   * All employees are direct employees of martinus, with the exceptions of "Gestellung" there the workers are external from the Bistum Mainz but do not count as external contract workers.
   */
  params.arbeitnehmerueberlassung ??= false;

  /**
   * The taetigkeit is required to calculate the Tätigkeitsschlüssel.
   */
  if (!params.taetigkeit) {
    return '';
  }

  /**
   * Init String with all 9 as this is the fallback value for all fields that are not yet implemented.
   */
  let taetigkeitsschluessel = Array(9).fill('9').join('');

  // TODO generate taetigkeits schlüssel 0-4 from the taetigkeit, but i dont have the mapping?
  taetigkeitsschluessel = replaceAtIndexRange(
    taetigkeitsschluessel,
    getTaetigkeitsTestIdentifikationscode(params.taetigkeit),
    ...TAETIGKEITSSCHLUESSEL_INDEX_MAP.taetigkeit,
  );

  taetigkeitsschluessel = replaceAtIndexRange(
    taetigkeitsschluessel,
    getIdentifikationscode(Schulabschluss, params.hoechsterSchulabschluss ?? '').toString(),
    ...TAETIGKEITSSCHLUESSEL_INDEX_MAP.schulabschluss,
  );

  taetigkeitsschluessel = replaceAtIndexRange(
    taetigkeitsschluessel,
    getIdentifikationscode(Berufsabschluss, params.hoechsterBerufsabschluss ?? '').toString(),
    ...TAETIGKEITSSCHLUESSEL_INDEX_MAP.berufsabschluss,
  );

  /**
   * 1 = keine Arbeitnehmerüberlassung
   * 2 = Arbeitnehmerüberlassung
   */
  taetigkeitsschluessel = replaceAtIndexRange(
    taetigkeitsschluessel,
    params.arbeitnehmerueberlassung ? '2' : '1',
    ...TAETIGKEITSSCHLUESSEL_INDEX_MAP.arbeitnehmerueberlassung,
  );

  taetigkeitsschluessel = replaceAtIndexRange(
    taetigkeitsschluessel,
    getIdentifikationscode(Vertragsform, params.vertragsform ?? '').toString(),
    ...TAETIGKEITSSCHLUESSEL_INDEX_MAP.vertragsform,
  );

  return taetigkeitsschluessel;
}
